import { Box, Button, ButtonBase, IconButton } from "@mui/material";
import { alpha } from "@mui/material/styles";
import type { DynamicForm, FormSubmit } from "api";
import { motion } from "framer-motion";
import React, { useCallback, useEffect, useRef, useState } from "react";
import type { FormState, Path, UseFormTrigger } from "react-hook-form";
import { get } from "react-hook-form";
import type { DynamicFormPages } from "./types";
import SendIcon from "@mui/icons-material/Send";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

type Props = {
  children?: React.ReactNode;
  pages: DynamicFormPages;
  pageIndex: number;
  pageKey: "intro" | "newsletter" | Path<FormSubmit>;
  onNext?: () => void;
  onPrevious?: () => void;
  onRendered?: () => void;
  onNavigate?: (pageIndex: number) => void;
  onPageTransitionEnd?: () => void;
  onPageTransitionStart?: () => void;
  onDirty?: () => void;
  onSubmit?: () => void;
  formState: FormState<FormSubmit>;
  trigger: UseFormTrigger<FormSubmit>;
  navigation?: DynamicForm["navigation"];
};

export const DynamicFormPage = ({
  onRendered,
  onDirty,
  onPageTransitionEnd,
  onPageTransitionStart,
  ...props
}: Props) => {
  const filteredDirtyFields = get<unknown>(
    props.formState.dirtyFields,
    props.pageKey,
  );
  const scrollContainer = document.querySelector("#form-scroll-container");
  const navigationContainerRef = useRef<HTMLDivElement | null>(null);
  const [scrollLeft, setScrollLeft] = React.useState(0);
  const [scrollRight, setScrollRight] = React.useState(0);
  const scrollAnimationRef = useRef<number | null>(null);
  const [bottomNavEl, setBottomNavEl] = useState<HTMLDivElement | null>(null);
  const [showFloatingNav, setShowFloatingNav] = useState(false);
  const bottomNavRef = useCallback((el: HTMLDivElement | null) => {
    setBottomNavEl(el);
  }, []);

  useEffect(() => {
    if (filteredDirtyFields) {
      onDirty?.();
    }
  }, [filteredDirtyFields, onDirty]);

  const startScrolling = (direction: "left" | "right") => {
    // Clear any existing animation
    if (scrollAnimationRef.current) {
      cancelAnimationFrame(scrollAnimationRef.current);
    }

    const container = navigationContainerRef.current;
    if (!container) return;

    const speed = 2; // pixels per frame

    const scrollSpeed = direction === "left" ? -speed : speed;

    const scroll = () => {
      container.scrollLeft += scrollSpeed;
      scrollAnimationRef.current = requestAnimationFrame(scroll);
    };

    scrollAnimationRef.current = requestAnimationFrame(scroll);
  };

  const stopScrolling = () => {
    if (scrollAnimationRef.current) {
      cancelAnimationFrame(scrollAnimationRef.current);
      scrollAnimationRef.current = null;
    }
  };

  const handleNext = async () => {
    if (props.pageKey == "intro" || props.pageKey == "newsletter") {
      props.onNext?.();
    } else {
      await props.trigger(props.pageKey);
      props.onNext?.();
    }
  };

  const handleBack = async () => {
    if (props.pageKey == "intro" || props.pageKey == "newsletter") {
      props.onPrevious?.();
    } else {
      await props.trigger(props.pageKey);
      props.onPrevious?.();
    }
  };

  const handleNavigate = async (index: number) => {
    if (props.pageKey == "intro" || props.pageKey == "newsletter") {
      props.onNavigate?.(index);
    } else {
      await props.trigger(props.pageKey);
      props.onNavigate?.(index);
    }
  };

  const handleResize = () => {
    const navigationContainer = navigationContainerRef.current;
    if (!navigationContainer) return;
    setScrollLeft(navigationContainer.scrollLeft - 1);
    setScrollRight(
      navigationContainer.scrollWidth -
        1 -
        navigationContainer.clientWidth -
        navigationContainer.scrollLeft,
    );
  };

  useEffect(() => {
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  useEffect(() => {
    const navigationContainer = navigationContainerRef.current;
    if (!navigationContainer) return;
    const navigation = navigationContainer.querySelector(".navigation");
    if (!navigation) return;

    const windowWidth = window.innerWidth;
    const navigationWidth = navigation.getBoundingClientRect().width;

    if (navigationWidth > windowWidth) {
      const activeDot = navigation.children[props.pageIndex] as HTMLElement;

      if (!activeDot) return;
      activeDot.scrollIntoView({
        behavior: "smooth",
        inline: "center",
      });
    }
  }, [props.pageIndex]);

  useEffect(() => {
    onRendered?.();
    return () => {
      stopScrolling();
    };
  }, [onRendered]);

  useEffect(() => {
    if (!bottomNavEl) {
      setShowFloatingNav(false);
      return;
    }
    const observer = new IntersectionObserver(
      ([entry]) => setShowFloatingNav(!entry.isIntersecting),
      { threshold: 0.1 },
    );
    observer.observe(bottomNavEl);
    return () => observer.disconnect();
  }, [bottomNavEl]);

  const pageInfos = props.pages.flatMap((page) => ({
    isSeen: page.isSeen,
    isDirty: page.isDirty,
    active: page.active,
  }));
  const pageErrors = props.pages.flatMap((page) => !!page.errors);

  const handleSubmit = async (e: React.MouseEvent<HTMLButtonElement>) => {
    // await props.trigger();
    if (props.pageKey !== "intro" && props.pageKey !== "newsletter") {
      const isValid = await props.trigger(props.pageKey);
      console.log("isValid", isValid);
    }

    const lastErrorIndex = pageErrors.indexOf(true);
    const lastUnseen = pageInfos.findIndex((p) => !p.isSeen);
    if (lastErrorIndex !== -1) {
      e.preventDefault();
      handleNavigate(lastErrorIndex);
      // props.trigger();
      return;
    } else if (lastUnseen !== -1) {
      e.preventDefault();
      handleNavigate(lastUnseen);
      return;
    }
    props.onSubmit?.();
  };

  const submitDisabled =
    props.formState.isSubmitting ||
    pageErrors.some((hasError) => hasError) ||
    !pageInfos.every((p) => p.isSeen === true);
  // !props.formState.isValid ||
  // !props.formState.isDirty ||
  // props.formState.isSubmitting ||
  // Object.values(props.formState.errors).length > 0

  console.log(props.formState.errors);

  return (
    <Box>
      <Box
        sx={(theme) => ({
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          zIndex: theme.zIndex.appBar,
          backdropFilter: "blur(16px)",
          WebkitBackdropFilter: "blur(16px)",
          backgroundColor: alpha(theme.palette.background.default, 0.82),
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
          boxShadow: `0 1px 12px ${alpha(theme.palette.common.black, 0.06)}`,
        })}
      >
        <Box sx={{ maxWidth: "900px", mx: "auto", px: { xs: 2, sm: 3 } }}>
          <Box
            sx={{
              position: "relative",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
        <Box
          sx={(theme) => ({
            opacity: scrollLeft > 0 ? 1 : 0,
            position: "absolute",
            left: 0,
            top: 0,
            bottom: 0,
            touchAction: "none",
            pointerEvents: scrollLeft > 0 ? "auto" : "none",
            zIndex: 10,
            background: `linear-gradient(to right, ${theme.palette.background.default}, transparent)`,
            width: "48px",
            height: "48px",
            backdropFilter: "blur(1px)",
            transition: theme.transitions.create(["opacity"], {
              duration: theme.transitions.duration.standard,
            }),
            userSelect: "none",
          })}
        >
          <Box
            onMouseEnter={() => startScrolling("left")}
            onMouseLeave={() => stopScrolling()}
            onTouchStart={() => startScrolling("left")}
            onTouchEnd={() => stopScrolling()}
            sx={{
              width: "48px",
              height: "48px",
              display: "flex",
              alignItems: "center",
              justifyContent: "start",
              cursor: "pointer",
            }}
          >
            <ChevronLeftIcon />
          </Box>
        </Box>

        <Box
          ref={navigationContainerRef}
          onScroll={(e) => {
            setScrollLeft(e.currentTarget.scrollLeft - 1);
            setScrollRight(
              e.currentTarget.scrollWidth -
                1 -
                e.currentTarget.clientWidth -
                e.currentTarget.scrollLeft,
            );
          }}
          sx={{
            height: "48px",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            position: "relative",
            overflowX: "auto",
            width: "100%",
            maxWidth: "100%",
            scrollbarWidth: "none",
            "&::-webkit-scrollbar": {
              display: "none",
            },
          }}
        >
          <Box
            sx={{
              maxWidth: "100%",
            }}
          >
            <Box
              className="navigation"
              sx={(theme) => ({
                display: "inline-flex",
                justifyContent: "space-evenly",
                alignItems: "center",
                // height: 64,
                // maxWidth: "100%",
                gap: 4,
                // width: "100%",

                padding: 2,
                // px: navigationScroll ? "48px" : 2,

                // pt: 2,
              })}
            >
              {props.pages.map((page, index) => {
                const pageErrors = !!page.errors;

                return (
                  <ButtonBase
                    component="div"
                    key={index}
                    onClick={() => handleNavigate(index)}
                    sx={(theme) => ({
                      display: "inline-block",
                      width: 15,
                      height: 15,
                      flexShrink: 0,

                      marginBottom: 0,
                      margin: 0,
                      borderRadius: "50%",
                      ":hover": {
                        opacity: 1,
                        transform: "scale(1.25)",
                      },
                      opacity: index === props.pageIndex ? 1 : 0.7,
                      transform:
                        index === props.pageIndex ? "scale(1.5)" : "scale(1)",
                      backgroundColor:
                        index === props.pageIndex
                          ? theme.palette.primary.main
                          : pageErrors
                            ? theme.palette.error.main
                            : page.isSeen
                              ? theme.palette.success.main
                              : theme.palette.action.disabled,
                      transition: [
                        theme.transitions.create(["background-color"], {
                          duration: theme.transitions.duration.standard,
                        }),
                        theme.transitions.create(["transform"], {
                          duration: "0.15s",
                        }),
                      ],
                      mb: 4,
                    })}
                  />
                );
              })}
            </Box>
          </Box>
        </Box>
        <Box
          sx={(theme) => ({
            opacity: scrollRight > 0 ? 1 : 0,
            position: "absolute",
            right: 0,
            top: 0,
            bottom: 0,
            touchAction: "none",
            pointerEvents: scrollRight > 0 ? "auto" : "none",
            zIndex: 10,
            background: `linear-gradient(to left, ${theme.palette.background.default}, transparent)`,
            width: "48px",
            height: "48px",
            backdropFilter: "blur(1px)",
            transition: theme.transitions.create(["opacity"], {
              duration: theme.transitions.duration.standard,
            }),
            userSelect: "none",
          })}
        >
          <Box
            onMouseEnter={() => startScrolling("right")}
            onMouseLeave={() => stopScrolling()}
            onTouchStart={() => startScrolling("right")}
            onTouchEnd={() => stopScrolling()}
            sx={{
              width: "48px",
              height: "48px",
              display: "flex",
              alignItems: "center",
              justifyContent: "end",
            }}
          >
            <ChevronRightIcon sx={{ cursor: "pointer" }} />
          </Box>
        </Box>
          </Box>
        </Box>
      </Box>
      <Box sx={{ height: 72 }} />
      <motion.div
        key={props.pageKey}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.25, delay: 0.05 }}
        onAnimationComplete={() => {
          onPageTransitionEnd?.();
        }}
        onAnimationStart={() => {
          requestAnimationFrame(() => {
            scrollContainer?.scrollTo({ top: 0, behavior: "instant" });
          });
          onPageTransitionStart?.();
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <Box>{props.children}</Box>
          <Box ref={bottomNavRef} sx={{ display: "flex", justifyContent: "space-between" }}>
            <>
              <Button
                startIcon={<ChevronLeftIcon />}
                onClick={handleBack}
                variant="contained"
                disabled={props.pageIndex === 0}
                sx={{
                  borderRadius: 64,
                  opacity: props.pageIndex === 0 ? 0 : 1,
                  pointerEvents: props.pageIndex === 0 ? "none" : "auto",
                  display: { xs: "none", sm: "flex" },
                }}
              >
                {props.navigation?.back ?? "Back"}
              </Button>
              <IconButton
                onClick={handleBack}
                sx={(theme) => ({
                  display: { xs: "flex", sm: "none", aspectRatio: "1/1" },
                  backgroundColor: theme.palette.primary.main,
                  color: theme.palette.primary.contrastText,
                  p: 1,
                  "&:active": {
                    backgroundColor: theme.palette.primary.dark,
                  },
                  "&:hover": {
                    backgroundColor: theme.palette.primary.dark,
                  },
                  "&:disabled": {
                    backgroundColor: theme.palette.action.disabledBackground,
                    color: theme.palette.action.disabled,
                  },
                })}
                size="large"
              >
                <ChevronLeftIcon color="inherit" />
              </IconButton>
            </>
            {props.pageIndex !== props.pages.length - 1 ? (
              <>
                <Button
                  onClick={handleNext}
                  endIcon={<ChevronRightIcon />}
                  variant="contained"
                  color="primary"
                  sx={{
                    borderRadius: 64,
                    display: { xs: "none", sm: "flex" },
                  }}
                >
                  {props.navigation?.forward ?? "Next"}
                </Button>
                <IconButton
                  onClick={handleNext}
                  sx={(theme) => ({
                    display: { xs: "flex", sm: "none", aspectRatio: "1/1" },
                    backgroundColor: theme.palette.primary.main,
                    color: theme.palette.primary.contrastText,
                    p: 1,
                    "&:active": {
                      backgroundColor: theme.palette.primary.dark,
                    },
                    "&:hover": {
                      backgroundColor: theme.palette.primary.dark,
                    },
                    "&:disabled": {
                      backgroundColor: theme.palette.action.disabledBackground,
                      color: theme.palette.action.disabled,
                    },
                  })}
                  size="large"
                >
                  <ChevronRightIcon color="inherit" />
                </IconButton>
              </>
            ) : (
              <>
                <Button
                  endIcon={<SendIcon />}
                  loading={props.formState.isSubmitting}
                  loadingPosition="end"
                  onClick={handleSubmit}
                  type="submit"
                  variant="contained"
                  color="success"
                  sx={{ borderRadius: 64, display: { xs: "none", sm: "flex" } }}
                  disabled={props.formState.isSubmitting}
                >
                  {props.navigation?.submit ?? "Submit"}
                </Button>
                <IconButton
                  onClick={handleSubmit}
                  sx={(theme) => ({
                    display: { xs: "flex", sm: "none", aspectRatio: "1/1" },
                    backgroundColor: theme.palette.success.main,
                    color: theme.palette.success.contrastText,
                    p: 1,
                    "&:active": {
                      backgroundColor: theme.palette.success.dark,
                    },
                    "&:hover": {
                      backgroundColor: theme.palette.success.dark,
                    },
                    "&:disabled": {
                      backgroundColor: theme.palette.action.disabledBackground,
                      color: theme.palette.action.disabled,
                    },
                  })}
                  disabled={props.formState.isSubmitting}
                  type={"submit"}
                  size="medium"
                >
                  <SendIcon
                    color="inherit"
                    fontSize={"small"}
                    sx={{ position: "relative", right: -2 }}
                  />
                </IconButton>
              </>
            )}
          </Box>
        </Box>
      </motion.div>
      {/* Left side floating back button — desktop only (lg+) */}
      <Box
        sx={(theme) => ({
          position: "fixed",
          top: "50vh",
          // sits 16px to the left of the md container edge (50vw - 450px)
          left: "max(8px, calc(50vw - 514px))",
          transform:
            showFloatingNav && props.pageIndex > 0
              ? "translateY(-50%) translateX(0)"
              : "translateY(-50%) translateX(-16px)",
          opacity: showFloatingNav && props.pageIndex > 0 ? 1 : 0,
          pointerEvents:
            showFloatingNav && props.pageIndex > 0 ? "auto" : "none",
          zIndex: theme.zIndex.fab,
          display: { xs: "none", lg: "flex" },
          transition: theme.transitions.create(["opacity", "transform"], {
            duration: theme.transitions.duration.standard,
          }),
        })}
      >
        <IconButton
          onClick={handleBack}
          size="large"
          sx={(theme) => ({
            backgroundColor: theme.palette.primary.main,
            color: theme.palette.primary.contrastText,
            boxShadow: theme.shadows[4],
            "&:hover": {
              backgroundColor: theme.palette.primary.dark,
              boxShadow: theme.shadows[6],
            },
          })}
        >
          <ChevronLeftIcon />
        </IconButton>
      </Box>

      {/* Right side floating forward/submit button — desktop only (lg+) */}
      <Box
        sx={(theme) => ({
          position: "fixed",
          top: "50vh",
          // mirror of left: 16px to the right of the md container edge
          right: "max(8px, calc(50vw - 514px))",
          transform: showFloatingNav
            ? "translateY(-50%) translateX(0)"
            : "translateY(-50%) translateX(16px)",
          opacity: showFloatingNav ? 1 : 0,
          pointerEvents: showFloatingNav ? "auto" : "none",
          zIndex: theme.zIndex.fab,
          display: { xs: "none", lg: "flex" },
          transition: theme.transitions.create(["opacity", "transform"], {
            duration: theme.transitions.duration.standard,
          }),
        })}
      >
        {props.pageIndex !== props.pages.length - 1 ? (
          <IconButton
            onClick={handleNext}
            size="large"
            sx={(theme) => ({
              backgroundColor: theme.palette.primary.main,
              color: theme.palette.primary.contrastText,
              boxShadow: theme.shadows[4],
              "&:hover": {
                backgroundColor: theme.palette.primary.dark,
                boxShadow: theme.shadows[6],
              },
            })}
          >
            <ChevronRightIcon />
          </IconButton>
        ) : (
          <IconButton
            onClick={handleSubmit}
            type="submit"
            disabled={props.formState.isSubmitting}
            size="large"
            sx={(theme) => ({
              backgroundColor: theme.palette.success.main,
              color: theme.palette.success.contrastText,
              boxShadow: theme.shadows[4],
              "&:hover": {
                backgroundColor: theme.palette.success.dark,
                boxShadow: theme.shadows[6],
              },
              "&:disabled": {
                backgroundColor: theme.palette.action.disabledBackground,
                color: theme.palette.action.disabled,
                boxShadow: "none",
              },
            })}
          >
            <SendIcon fontSize="small" sx={{ position: "relative", right: -2 }} />
          </IconButton>
        )}
      </Box>

      {/* Mobile/tablet floating bottom pill — hidden on lg+ */}
      <Box
        sx={(theme) => ({
          position: "fixed",
          bottom: 16,
          left: "50%",
          transform: showFloatingNav
            ? "translateX(-50%) translateY(0)"
            : "translateX(-50%) translateY(12px)",
          opacity: showFloatingNav ? 1 : 0,
          pointerEvents: showFloatingNav ? "auto" : "none",
          zIndex: theme.zIndex.fab,
          display: { xs: "flex", lg: "none" },
          gap: 1,
          alignItems: "center",
          backgroundColor: theme.palette.background.paper,
          borderRadius: 64,
          p: 0.75,
          boxShadow: theme.shadows[8],
          transition: theme.transitions.create(["opacity", "transform"], {
            duration: theme.transitions.duration.standard,
          }),
        })}
      >
        <IconButton
          onClick={handleBack}
          size="medium"
          sx={(theme) => ({
            backgroundColor: theme.palette.primary.main,
            color: theme.palette.primary.contrastText,
            opacity: props.pageIndex === 0 ? 0 : 1,
            pointerEvents: props.pageIndex === 0 ? "none" : "auto",
            transition: theme.transitions.create(["opacity"], {
              duration: theme.transitions.duration.standard,
            }),
            "&:hover": { backgroundColor: theme.palette.primary.dark },
          })}
        >
          <ChevronLeftIcon />
        </IconButton>
        {props.pageIndex !== props.pages.length - 1 ? (
          <IconButton
            onClick={handleNext}
            size="medium"
            sx={(theme) => ({
              backgroundColor: theme.palette.primary.main,
              color: theme.palette.primary.contrastText,
              "&:hover": { backgroundColor: theme.palette.primary.dark },
            })}
          >
            <ChevronRightIcon />
          </IconButton>
        ) : (
          <IconButton
            onClick={handleSubmit}
            type="submit"
            disabled={props.formState.isSubmitting}
            size="medium"
            sx={(theme) => ({
              backgroundColor: theme.palette.success.main,
              color: theme.palette.success.contrastText,
              "&:hover": { backgroundColor: theme.palette.success.dark },
              "&:disabled": {
                backgroundColor: theme.palette.action.disabledBackground,
                color: theme.palette.action.disabled,
              },
            })}
          >
            <SendIcon fontSize="small" sx={{ position: "relative", right: -2 }} />
          </IconButton>
        )}
      </Box>
    </Box>
  );
};
